import type { CircleBranding } from './circles';
import api, { apiNoAuth } from './baseApi';

export type OthRegistrationFieldType =
  'text' | 'email' | 'textarea' | 'select' | 'radio' | 'checkbox';

export type OthRegistrationField = {
  id: string;
  label: string;
  type: OthRegistrationFieldType;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
  options?: string[];
};

export type OthRegistrationSection = {
  id: string;
  title: string;
  description?: string;
  fields: OthRegistrationField[];
};

export type OthRegistrationSchemaJson = {
  title: string;
  description?: string;
  sections: OthRegistrationSection[];
};

export type OthRegistrationSchema = {
  id: string;
  tribe_id: string;
  tribe_name?: string;
  description?: string;
  schemaJson: OthRegistrationSchemaJson;
  /** The circle's look; only present when the circle has branding. */
  branding?: CircleBranding;
  createdAt: string;
  updatedAt: string;
};

export type SubmitOthRegistrationPayload = {
  first_name?: string;
  surname?: string;
  email?: string;
  firm?: string;
  job_title?: string;
  industry?: string;
  leadership_level?: string;
  gender?: string;
  answers: Record<string, unknown>;
};

export type SubmitOthRegistrationResult = {
  submissionId: string;
  schemaId: string;
  tribeId: string;
  userId: string;
  wasAlreadyMember: boolean;
};

export type OthRegistrationSubmission = {
  id: string;
  user_id: string;
  first_name: string;
  surname: string;
  phone_number: string;
  email: string | null;
  firm: string | null;
  answers: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type OthRegistrationSubmissionListResult = {
  schema: OthRegistrationSchemaJson;
  submissions: OthRegistrationSubmission[];
};

export type OthWelcomeEmailTemplate = {
  subject: string;
  body: string;
};

type ApiEnvelope<T> = {
  result: T;
};

export async function getOthRegistrationSchema(
  tribeId: string
): Promise<OthRegistrationSchema> {
  const { data } = await apiNoAuth.get<ApiEnvelope<OthRegistrationSchema>>(
    `tribes/${encodeURIComponent(tribeId)}/oth-registration/schema`
  );
  return data.result;
}

export async function submitOthRegistration(
  tribeId: string,
  payload: SubmitOthRegistrationPayload
): Promise<SubmitOthRegistrationResult> {
  const { data } = await api.post<ApiEnvelope<SubmitOthRegistrationResult>>(
    `oth/registration/${encodeURIComponent(tribeId)}/submit`,
    payload
  );
  return data.result;
}

export async function listOthRegistrationSubmissions(
  tribeId: string
): Promise<OthRegistrationSubmissionListResult> {
  const { data } = await api.get<
    ApiEnvelope<OthRegistrationSubmissionListResult>
  >(`tribes/${encodeURIComponent(tribeId)}/oth-registration/submissions`);
  return data.result;
}

export async function exportOthRegistrationSubmissionsCsv(
  tribeId: string
): Promise<Blob> {
  const response = await api.get(
    `tribes/${encodeURIComponent(tribeId)}/oth-registration/submissions/export`,
    { responseType: 'blob' }
  );
  return response.data as Blob;
}

export async function deleteOthRegistrationSubmission(
  tribeId: string,
  submissionId: string
): Promise<void> {
  await api.delete(
    `tribes/${encodeURIComponent(tribeId)}/oth-registration/submissions/${encodeURIComponent(submissionId)}`
  );
}

export async function getOthWelcomeEmail(
  tribeId: string
): Promise<OthWelcomeEmailTemplate> {
  const { data } = await api.get<ApiEnvelope<OthWelcomeEmailTemplate>>(
    `tribes/${encodeURIComponent(tribeId)}/oth-registration/welcome-email`
  );
  return data.result;
}

export async function updateOthWelcomeEmail(
  tribeId: string,
  payload: OthWelcomeEmailTemplate
): Promise<OthWelcomeEmailTemplate> {
  const { data } = await api.put<ApiEnvelope<OthWelcomeEmailTemplate>>(
    `tribes/${encodeURIComponent(tribeId)}/oth-registration/welcome-email`,
    payload
  );
  return data.result;
}

/** Global default used by circles that have no circle-specific welcome email. */
export async function updateOthWelcomeEmailDefault(
  payload: OthWelcomeEmailTemplate
): Promise<OthWelcomeEmailTemplate> {
  const { data } = await api.put<ApiEnvelope<OthWelcomeEmailTemplate>>(
    'oth-registration/welcome-email/default',
    payload
  );
  return data.result;
}
